import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgModule,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { BehaviorSubject, fromEvent, Subject } from 'rxjs';
import { delay, filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { SafeUrlPipeModule } from '../../pipes/safe-url.pipe';
import { ActivityPlayerEvent } from '../../shared/activity-player/activity-player-event.enum';
import { ChatBotEventService } from '../../shared/chat-bot-event.service';
import { ChatBotStateService } from '../../shared/chat-bot-state.service';
import { GTMService } from '../../shared/gtm.service';
import { isIFrameSameOrigin } from '../../shared/helpers/is-same-origin';
import { OeAppPersonService } from '../../shared/oe-app-person.service';
import { OeAppService } from '../../shared/oe-app.service';
import { OneSignalService } from '../../shared/one-signal.service';
import { ProfileServiceConfigurationService } from '../../shared/profile-service-configuration.service';
import { ChatBotEventType } from '../../shared/types/chat-bot-event.enum';
import { ChatBotTrackEvents } from '../../shared/types/chat-bot-track-events.enum';
import { IFrameSizeEventType } from '../../shared/types/iframe-event-type';
import { MediaEventType } from '../../shared/types/media-event.enum';
import { ChatBotTypes } from '../../shared/types/chat-bot-types.enum';
import { OneSignalTags } from '../../shared/types/one-signal-tags';
import { UserwaySyncModule } from '../../shared/userway-sync.directive';
import { PrivateClassBannerEventType } from '../../shared/types/live-class-events.enum';

const DISABLED_HINTS_CHAT_TYPES = [ChatBotTypes.LESSON, ChatBotTypes.LC_FEEDBACK];
const MINIMUM_HEIGHT = 640;

const IGNORED_EVENT_TYPES: string[] = [
  ActivityPlayerEvent.REQUEST_LESSON_INFO,
  ActivityPlayerEvent.REQUEST_STUDENT_INFO,
  ActivityPlayerEvent.ACTIVITY_COMPLETE,
  ActivityPlayerEvent.LESSON_COMPLETE,
  ActivityPlayerEvent.FINISH_LESSON,
  ActivityPlayerEvent.NAVIGATE_HOME,
  ActivityPlayerEvent.REFERRAL_BUTTON_CLICKED,
  IFrameSizeEventType.HEIGHT,
  PrivateClassBannerEventType.ON_RESIZE_EVENT,
];

@Component({
  selector: 'lp2-chat-bot-iframe',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <iframe
      #iframe
      lp2UserwaySync
      *ngIf="chatBotUrl$ | async as url"
      [src]="url | safeUrl"
      frameborder="0"
      [height]="height"
      allow="microphone; autoplay"
      class="chat-bot-modal-iframe"
      [class.chat-bot-modal-iframe--fast]="chatType === chatBotTypes.LC_FEEDBACK"
    >
    </iframe>
  `,
  styleUrls: ['./chat-bot-iframe.component.scss'],
})
export class ChatBotIframeComponent implements OnInit, AfterViewInit {
  @Input() chatType: string;
  @Output() close: EventEmitter<void> = new EventEmitter();
  @Output() onboardingComplete: EventEmitter<void> = new EventEmitter();
  @ViewChild('iframe') public iframe: ElementRef<HTMLIFrameElement>;
  public chatBotTypes = ChatBotTypes;
  public height: number | string = MINIMUM_HEIGHT;
  private iframeLoaded$ = new BehaviorSubject<boolean>(false);
  private destroyed$ = new Subject<void>();

  constructor(
    private gtmService: GTMService,
    private oeApp: OeAppService,
    public oeAppPersonService: OeAppPersonService,
    private readonly profileServiceConfigurationService: ProfileServiceConfigurationService,
    private cdRef: ChangeDetectorRef,
    private chatBotStateService: ChatBotStateService,
    private chatBotEventService: ChatBotEventService,
    private oneSignalService: OneSignalService
  ) {}

  ngOnInit() {
    window.addEventListener('message', this.handleEvents, false);
  }

  ngAfterViewInit(): void {
    this.chatBotUrl$
      .pipe(
        filter((url) => !!url),
        tap(() => this.cdRef.detectChanges()),
        switchMap(() => {
          return fromEvent(this.iframe.nativeElement, 'load');
        }),
        delay(1000)
      )
      .subscribe(() => {
        this.iframeLoaded$.next(true);
      });
    this.postOpenMessageOnShowModal();
    this.subscribeToChatBotEventMessages();
  }

  ngOnDestroy() {
    this.destroyed$.next();
    window.removeEventListener('message', this.handleEvents);
    this.chatBotEventService.clearEvent();
  }

  public get chatBotUrl$() {
    return this.profileServiceConfigurationService.state$.pipe(
      filter((config) => !!config),
      map((config) => {
        const { copilotAutoplayEnabled, copilotAudioStreamEnabled } = config;
        const environment = this.oeApp.environment;
        const contactId = this.oeAppPersonService.contactId;
        const personId = this.oeAppPersonService.id;

        const chatTypeParam = this.chatType ? '&chatType=' + this.chatType : '';
        const disabledHintsChatTypesParam = '&disabledHintsChatTypes=' + DISABLED_HINTS_CHAT_TYPES.join(',');

        if (environment && contactId) {
          return (
            this.oeApp.webChatBotPageHost +
            '?env=' +
            environment +
            '&contactId=' +
            contactId +
            '&personId=' +
            personId +
            '&lang=' +
            this.oeAppPersonService.preferredLang +
            '&hasSeenCopilotOnboarding=' +
            this.oeAppPersonService.hasSeenCopilotOnboarding +
            '&autoPlayEnabledKillSwitch=' +
            copilotAutoplayEnabled +
            '&copilotAudioStreamEnabled=' +
            copilotAudioStreamEnabled +
            chatTypeParam +
            disabledHintsChatTypesParam
          );
        }
      })
    );
  }

  private closeChatBot() {
    this.iframe.nativeElement.contentWindow.postMessage({ type: MediaEventType.STOP_ALL_VIDEOS }, '*');
    this.iframe.nativeElement.contentWindow.postMessage({ type: MediaEventType.STOP_ALL_AUDIOS }, '*');
    this.iframe.nativeElement.contentWindow.postMessage({ type: MediaEventType.STOP_ALL_RECORDINGS }, '*');
    setTimeout(() => {
      this.close.emit();
    }, 100); // time for event propagation
  }

  public handleEvents = ({ data, origin }) => {
    if (
      !isIFrameSameOrigin(
        [this.oeApp.webChatBotPageHost, this.oeApp.activityPlayerHost(), this.oeApp.webLessonPlayerPageHost],
        origin
      )
    ) {
      return;
    }

    if (!data || typeof data.type !== 'string') {
      return;
    }

    switch (data.type) {
      case ChatBotEventType.CHAT_BOT_CLOSE_EVENT:
        this.gtmService.sendGA4Event(ChatBotTrackEvents.COPILOT_CLOSE);
        this.closeChatBot();
        break;

      case IFrameSizeEventType.WEB_CHAT_BOT_HEIGHT:
        this.height = Math.max(data.height, MINIMUM_HEIGHT);
        this.cdRef.detectChanges();
        break;

      case ChatBotEventType.COPILOT_TRACKING_EVENT:
        this.gtmService.sendGA4Event(data.data.event);
        if (data.data.event === ChatBotTrackEvents.COPILOT_SEND_TEXT) {
          this.oneSignalService.setTag(OneSignalTags.LAST_JENNY_CONVERSATION_INITIATED, Date.now().toString());
        }
        break;

      case ChatBotEventType.COPILOT_OUTCOME_EVENT:
        this.oneSignalService.trackOutcome(data.data.event);
        break;

      case ChatBotEventType.CHAT_BOT_ONBOARDING_COMPLETE:
        this.onboardingComplete.emit();
        break;

      case MediaEventType.STOP_ALL_AUDIOS:
        break;

      case ActivityPlayerEvent.OPEN_CHAT_BOT_LESSON: {
        const dataValue = data.value || data.data;
        const lessonInfo = dataValue && dataValue.lessonInfo;
        this.gtmService.sendGA4Event(ChatBotTrackEvents.COPILOT_ASK_JENNY_MINILESSONS);

        this.postMessage({
          type: ActivityPlayerEvent.SEND_CHAT_BOT_LESSON_INFO,
          value: { lessonInfo },
        });

        this.chatBotStateService.openChatBotMiniLessonModal();
        break;
      }

      case ActivityPlayerEvent.SLIDE_CHANGED: {
        this.closeChatBot();
        break;
      }

      default:
        if (!IGNORED_EVENT_TYPES.includes(data.type)) {
          console.error('Event type not handled: ', data);
        }
        break;
    }
  };

  private postMessage(message: any) {
    this.iframe.nativeElement.contentWindow.postMessage(message, '*');
  }

  private postOpenMessage() {
    this.postMessage({ type: ChatBotEventType.CHAT_BOT_OPEN_EVENT });
  }

  private postOpenMessageOnShowModal() {
    this.iframeLoaded$
      .pipe(
        filter((loaded) => loaded),
        switchMap(() => this.chatBotStateService.state$),
        filter((state) => state.showModal || state.showLcFeedbackModal || state.showMiniLessonModal),
        takeUntil(this.destroyed$)
      )
      .subscribe(() => {
        this.postOpenMessage();
      });
  }

  private subscribeToChatBotEventMessages() {
    this.iframeLoaded$
      .pipe(
        filter((loaded) => loaded),
        switchMap(() => this.chatBotEventService.messages$),
        filter((message) => !!message),
        takeUntil(this.destroyed$)
      )
      .subscribe((message) => {
        this.postMessage(message);
      });
  }
}

@NgModule({
  imports: [CommonModule, SafeUrlPipeModule, UserwaySyncModule],
  declarations: [ChatBotIframeComponent],
  exports: [ChatBotIframeComponent],
})
export class ChatBotIframeModule {}



// WEBPACK FOOTER //
// ./ts/app/components/chat-bot-iframe/chat-bot-iframe.component.ts